import { KAKSHA_LORDS, PLANETS, SIGNS } from '../constants.js';
import {
  clearElement,
  enrichPlanetList,
  formatDegreeInSign,
  htmlElement,
} from '../utils.js';

export function renderGocharaKaksha(container, snapshot) {
  clearElement(container);
  const transitPlanets = enrichPlanetList(snapshot?.transit?.planets || [])
    .filter((planet) => planet.id !== 'ascendant' && !['rahu', 'ketu', 'uranus', 'neptune', 'pluto'].includes(planet.id));
  const suppliedRows = Array.isArray(snapshot?.kaksha) ? snapshot.kaksha : [];
  const rowMap = new Map(suppliedRows.map((row) => [String(row.planetId || row.id || '').toLowerCase(), row]));

  const grid = htmlElement('div', { className: 'kaksha-grid' });
  SIGNS.forEach((sign, signIndex) => {
    const card = htmlElement('section', { className: 'kaksha-sign-card' });
    card.append(htmlElement('header', { className: 'kaksha-sign-header' }, [
      htmlElement('span', { className: 'kaksha-sign-glyph', text: sign.glyph }),
      htmlElement('strong', { text: sign.name }),
    ]));
    const segments = htmlElement('div', { className: 'kaksha-segments' });
    KAKSHA_LORDS.forEach((lord, index) => {
      const segment = htmlElement('div', {
        className: 'kaksha-segment',
        dataset: { lord: lord.toLowerCase(), index: String(index) },
      });
      segment.append(htmlElement('span', { className: 'kaksha-lord', text: lord.slice(0, 2) }));
      const occupants = transitPlanets.filter((planet) => planet.signIndex === signIndex && kakshaIndex(planet.degreeInSign) === index);
      occupants.forEach((planet) => {
        const meta = PLANETS[planet.id] || planet;
        const badge = htmlElement('span', {
          className: `kaksha-planet ${meta.className || 'planet-generic'}`,
          title: `${planet.name} ${formatDegreeInSign(planet.degreeInSign)} ${planet.sign} — ${lord} Kaksha`,
          text: `${planet.short}${planet.retrograde ? 'R' : ''}`,
        });
        segment.append(badge);
      });
      segments.append(segment);
    });
    card.append(segments);
    grid.append(card);
  });

  const table = buildKakshaTable(transitPlanets, rowMap);
  const notes = htmlElement('div', { className: 'aat-note' }, [
    htmlElement('strong', { text: 'Calculation hook: ' }),
    'each sign is divided into eight 3°45′ Kakshas. BAV/SAV values come from Kundlistar’s existing Ashtakavarga calculation through the provider adapter; this module does not reimplement bindu logic.',
  ]);
  container.append(grid, table, notes);
  return { grid, table };
}

function buildKakshaTable(planets, rowMap) {
  const table = htmlElement('table', { className: 'aat-data-table kaksha-table' });
  const head = htmlElement('thead');
  head.append(htmlElement('tr', {}, [
    htmlElement('th', { text: 'Transit' }),
    htmlElement('th', { text: 'Position' }),
    htmlElement('th', { text: 'Kaksha' }),
    htmlElement('th', { text: 'Kaksha bindu' }),
    htmlElement('th', { text: 'Planet BAV' }),
    htmlElement('th', { text: 'SAV' }),
  ]));
  const body = htmlElement('tbody');
  planets.forEach((planet) => {
    const supplied = rowMap.get(planet.id) || {};
    const index = Number.isInteger(supplied.kakshaIndex) ? supplied.kakshaIndex : kakshaIndex(planet.degreeInSign);
    const lord = supplied.kakshaLord || KAKSHA_LORDS[index];
    body.append(htmlElement('tr', {}, [
      htmlElement('td', {}, [
        htmlElement('span', { className: `table-planet ${planet.className || 'planet-generic'}`, text: planet.short }),
        ` ${planet.name}${planet.retrograde ? ' (R)' : ''}`,
      ]),
      htmlElement('td', { text: `${formatDegreeInSign(planet.degreeInSign)} ${planet.sign}` }),
      htmlElement('td', { text: `${index + 1} · ${lord}` }),
      htmlElement('td', { text: valueOrDash(supplied.kakshaBindu ?? supplied.bindu) }),
      htmlElement('td', { text: valueOrDash(supplied.bav ?? supplied.ashtakavarga) }),
      htmlElement('td', { text: valueOrDash(supplied.sav ?? supplied.sarvashtakavarga) }),
    ]));
  });
  table.append(head, body);
  return table;
}

function kakshaIndex(degreeInSign) {
  return Math.min(7, Math.floor((((Number(degreeInSign) % 30) + 30) % 30) / 3.75));
}

function valueOrDash(value) {
  return value === null || value === undefined || value === '' ? '—' : String(value);
}
